/**
 * Priority countries for the phone field country selector (Issue #440).
 *
 * The full country list (names, dialling codes, flags) comes from
 * react-phone-number-input — we only maintain the priority block here.
 *
 * Priority block (US, AU, NZ, GB, CA) is a business rule from issue #440,
 * kept in one named constant in the exact order given — do NOT re-sort it.
 * The remainder is rendered in react-phone-number-input's default order.
 */
import { getCountries } from 'libphonenumber-js';

// Priority countries — issue #440, in this exact order. Business rule, not
// alphabetical. A later contributor must not "tidy" this into A-Z order.
export const PRIORITY_COUNTRIES = ['US', 'AU', 'NZ', 'GB', 'CA'];

// Default country — issue #440 decision: the first priority entry.
export const DEFAULT_COUNTRY = 'US';

/**
 * Build the ordered country list for react-phone-number-input's `countries` prop.
 * Priority block first (in PRIORITY_COUNTRIES order), then the remainder.
 * Memoized — built once, reused across renders.
 */
let _ordered = null;
export function getOrderedCountryCodes() {
    if (!_ordered) {
        const all = getCountries();
        const prioritySet = new Set(PRIORITY_COUNTRIES);
        const priority = PRIORITY_COUNTRIES.filter((code) => all.includes(code));
        const rest = all.filter((code) => !prioritySet.has(code));
        _ordered = [...priority, ...rest];
    }
    return _ordered;
}

/**
 * Sort a react-phone-number-input options array so the priority block is first.
 *
 * react-phone-number-input ignores the order of the `countries` prop (it only
 * uses it for filtering) and returns options in alphabetical order. This function
 * re-sorts the options array so the priority block (US, AU, NZ, GB, CA — issue
 * #440) appears at the top of the dropdown, then the remainder in the library's
 * original (alphabetical) order.
 *
 * @param {Array<{value: string, label: string}>} countryList — options from RPNInput
 * @returns {Array<{value: string, label: string}>} — priority block first, then rest
 */
export function sortCountryOptions(countryList) {
    const prioritySet = new Set(PRIORITY_COUNTRIES);
    const priority = PRIORITY_COUNTRIES.map((code) =>
        countryList.find((c) => c.value === code),
    ).filter(Boolean);
    const rest = countryList.filter((c) => !prioritySet.has(c.value));
    return [...priority, ...rest];
}
